import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  LinearProgress,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AttachMoney as MoneyIcon,
  Assignment as TaskIcon,
  Flag as PriorityIcon,
  Today as TodayIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, isPast, isFuture, differenceInDays } from 'date-fns';

const Todo = () => {
  const theme = useTheme();
  const [todos, setTodos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    dueDate: null,
    amount: '',
  });

  const priorities = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'high', label: 'High', color: 'error' },
  ];

  const categories = [
    { value: 'general', label: 'General', icon: <TaskIcon /> },
    { value: 'financial', label: 'Financial', icon: <MoneyIcon /> },
    { value: 'budget', label: 'Budget Review', icon: <TimelineIcon /> },
    { value: 'goal', label: 'Goal Setting', icon: <PriorityIcon /> },
  ];

  useEffect(() => {
    loadTodos();
  }, []);

  const loadTodos = () => {
    // Load from localStorage for now - can be replaced with API call
    const savedTodos = localStorage.getItem('financeTodos');
    if (savedTodos) {
      setTodos(JSON.parse(savedTodos));
    } else {
      // Default todos
      const defaultTodos = [
        {
          id: 1,
          title: 'Review Monthly Budget',
          description: 'Check spending against budget categories',
          priority: 'high',
          category: 'budget',
          completed: false,
          dueDate: new Date().toISOString(),
          amount: '',
          createdAt: new Date().toISOString(),
        },
        {
          id: 2,
          title: 'Set Emergency Fund Goal',
          description: 'Aim to save $10,000 for emergency fund',
          priority: 'medium',
          category: 'goal',
          completed: false,
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          amount: '10000',
          createdAt: new Date().toISOString(),
        },
      ];
      setTodos(defaultTodos);
      localStorage.setItem('financeTodos', JSON.stringify(defaultTodos));
    }
  };

  const saveTodos = (updatedTodos) => {
    localStorage.setItem('financeTodos', JSON.stringify(updatedTodos));
    setTodos(updatedTodos);
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      showSnackbar('Please enter a title', 'error');
      return;
    }

    const todoData = {
      ...formData,
      id: editingTodo ? editingTodo.id : Date.now(),
      completed: editingTodo ? editingTodo.completed : false,
      createdAt: editingTodo ? editingTodo.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let updatedTodos;
    if (editingTodo) {
      updatedTodos = todos.map(todo => 
        todo.id === editingTodo.id ? todoData : todo
      );
      showSnackbar('Todo updated successfully', 'success');
    } else {
      updatedTodos = [...todos, todoData];
      showSnackbar('Todo created successfully', 'success');
    }

    saveTodos(updatedTodos);
    handleClose();
  };

  const handleDelete = (id) => {
    const updatedTodos = todos.filter(todo => todo.id !== id);
    saveTodos(updatedTodos);
    showSnackbar('Todo deleted successfully', 'success');
  };

  const handleToggleComplete = (id) => {
    const updatedTodos = todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    );
    saveTodos(updatedTodos);
    const todo = todos.find(t => t.id === id);
    showSnackbar(
      `Todo ${todo.completed ? 'marked as incomplete' : 'completed'}`,
      'success'
    );
  };

  const handleEdit = (todo) => {
    setEditingTodo(todo);
    setFormData({
      title: todo.title,
      description: todo.description,
      priority: todo.priority,
      category: todo.category,
      dueDate: todo.dueDate ? new Date(todo.dueDate) : null,
      amount: todo.amount || '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTodo(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      category: 'general',
      dueDate: null,
      amount: '',
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getFilteredTodos = () => {
    switch (filter) {
      case 'completed':
        return todos.filter(todo => todo.completed);
      case 'pending':
        return todos.filter(todo => !todo.completed);
      case 'today':
        return todos.filter(todo => 
          todo.dueDate && isToday(new Date(todo.dueDate))
        );
      case 'overdue':
        return todos.filter(todo => 
          todo.dueDate && isPast(new Date(todo.dueDate)) && !todo.completed
        );
      default:
        return todos;
    }
  };

  const getStats = () => {
    const total = todos.length;
    const completed = todos.filter(todo => todo.completed).length;
    const pending = total - completed;
    const overdue = todos.filter(todo => 
      todo.dueDate && isPast(new Date(todo.dueDate)) && !todo.completed
    ).length;
    const today = todos.filter(todo => 
      todo.dueDate && isToday(new Date(todo.dueDate))
    ).length;

    return { total, completed, pending, overdue, today };
  };

  const getDueDateInfo = (dueDate) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    const days = differenceInDays(date, new Date());
    
    if (isToday(date)) {
      return { text: 'Due Today', color: 'warning', urgent: true };
    } else if (isPast(date)) {
      return { text: `Overdue by ${Math.abs(days)} day(s)`, color: 'error', urgent: true };
    } else if (days <= 3) {
      return { text: `Due in ${days} day(s)`, color: 'warning', urgent: false };
    } else {
      return { text: `Due ${format(date, 'MMM dd')}`, color: 'default', urgent: false };
    }
  };

  const stats = getStats();
  const filteredTodos = getFilteredTodos();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Financial Todo List
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Todo
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Tasks
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 'bold' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="h3" color="error.main" sx={{ fontWeight: 'bold' }}>
                {stats.overdue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Progress Bar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Overall Progress</Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.completed} of {stats.total} completed
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </CardContent>
        </Card>

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter Tasks</InputLabel>
            <Select
              value={filter}
              label="Filter Tasks"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All Tasks</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="today">Due Today</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Todo List */}
        <Card>
          <CardContent>
            {filteredTodos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TaskIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No tasks found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filter === 'all' ? 'Create your first todo item!' : `No ${filter} tasks at the moment.`}
                </Typography>
              </Box>
            ) : (
              <List>
                {filteredTodos.map((todo, index) => {
                  const dueDateInfo = getDueDateInfo(todo.dueDate);
                  const category = categories.find(c => c.value === todo.category);
                  const priority = priorities.find(p => p.value === todo.priority);

                  return (
                    <ListItem
                      key={todo.id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        backgroundColor: todo.completed ? 'action.hover' : 'background.paper',
                        opacity: todo.completed ? 0.7 : 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={todo.completed}
                          onChange={() => handleToggleComplete(todo.id)}
                          icon={<UncheckedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                          color="success"
                        />
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                textDecoration: todo.completed ? 'line-through' : 'none',
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {todo.title}
                            </Typography>
                            
                            <Chip
                              label={priority.label}
                              color={priority.color}
                              size="small"
                              variant="outlined"
                            />
                            
                            <Chip
                              icon={category.icon}
                              label={category.label}
                              size="small"
                              variant="outlined"
                            />
                            
                            {dueDateInfo && (
                              <Chip
                                label={dueDateInfo.text}
                                color={dueDateInfo.color}
                                size="small"
                                variant={dueDateInfo.urgent ? 'filled' : 'outlined'}
                              />
                            )}
                            
                            {todo.amount && (
                              <Chip
                                label={`$${parseFloat(todo.amount).toLocaleString()}`}
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {todo.description}
                          </Typography>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEdit(todo)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDelete(todo.id)} size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTodo ? 'Edit Todo' : 'Add New Todo'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Priority"
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      {priorities.map((priority) => (
                        <MenuItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      label="Category"
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={(date) => setFormData({ ...formData, dueDate: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    label="Amount (optional)"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    type="number"
                    fullWidth
                    InputProps={{
                      startAdornment: '$',
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingTodo ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            display: { xs: 'flex', sm: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      </Box>
    </LocalizationProvider>
  );
};

export default Todo; 